import React from 'react';
import { Button, Result } from 'antd';
import { ArrowsClockwiseIcon } from '@phosphor-icons/react';

// Stores the timestamp of the last auto-reload attempt.
// Using a timestamp (rather than a boolean) prevents infinite reload loops:
// if the same chunk keeps failing, the 15-second cooldown will expire before
// we try again, but a second failure within the cooldown window shows the
// error UI instead of looping.
const CHUNK_RELOAD_KEY = 'tm_chunk_reload_ts';
const RELOAD_COOLDOWN_MS = 15000;

function isChunkLoadError(error) {
    if (!error) return false;
    const msg = error.message || '';
    return (
        error.name === 'ChunkLoadError' ||
        msg.includes('Loading chunk') ||
        msg.includes('dynamically imported module') ||
        msg.includes('Failed to fetch dynamically imported module') ||
        msg.includes('Importing a module script failed')
    );
}

/**
 * Error Boundary that catches React render errors.
 *
 * ChunkLoadError (lazy route failed to load after deploy) → auto-reloads once
 * per 15-second cooldown window to avoid infinite loops on persistent failures.
 * Other errors → renders fallback prop (with injected onRefresh) or built-in Ant Design UI.
 *
 * Usage:
 *   <ErrorBoundary fallback={<MyFallback />}>  ← onRefresh injected automatically
 *   <ErrorBoundary>                             ← uses built-in UI
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, componentStack: null };
        this.handleRefresh = this.handleRefresh.bind(this);
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        console.error('[ErrorBoundary] Caught error:', error, info?.componentStack);
        this.setState({ componentStack: info?.componentStack ?? null });

        if (isChunkLoadError(error)) {
            const lastTs = sessionStorage.getItem(CHUNK_RELOAD_KEY);
            const elapsed = lastTs ? Date.now() - parseInt(lastTs, 10) : Infinity;
            if (elapsed > RELOAD_COOLDOWN_MS) {
                sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
                window.location.reload();
            }
            // Cooldown not elapsed → fall through to render error UI
        }
    }

    handleRefresh() {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        window.location.reload();
        window.location.href = "/login"; // fallback for browsers that don't reload on .reload()
    }

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        const { error, componentStack } = this.state;
        const { fallback } = this.props;
        if (fallback) {
            return React.cloneElement(fallback, {
                onRefresh: this.handleRefresh,
                error,
                componentStack,
            });
        }

        return (
            <DefaultErrorFallback
                onRefresh={this.handleRefresh}
                error={error}
                componentStack={componentStack}
            />
        );
    }
}

/**
 * Built-in fallback using Ant Design Result component.
 * Uses hardcoded Dutch/English text so it works outside IntlProvider.
 */
function DefaultErrorFallback({ onRefresh, error, componentStack }) {
    return (
        <Result
            status="error"
            title="Er is iets misgegaan / Something went wrong"
            subTitle="De applicatie is onverwacht gestopt. Ververs de pagina of wis de browsercache. / The app crashed unexpectedly. Please refresh or clear your browser cache."
            extra={[
                <ErrorDetails key="details" error={error} componentStack={componentStack} />,
                <Button
                    key="refresh"
                    type="primary"
                    icon={<ArrowsClockwiseIcon size={16} style={{ marginRight: 6 }} />}
                    onClick={onRefresh}
                    style={{ marginTop: 8 }}
                >
                    Vernieuwen / Refresh
                </Button>,
            ]}
        />
    );
}

/**
 * Shows the error message and a collapsible stack trace with a copy button.
 * Lets non-technical users share exact error details with support without opening DevTools.
 */
function ErrorDetails({ error, componentStack }) {
    const [copied, setCopied] = React.useState(false);

    if (!error) return null;

    const errorText = [
        `${error.name}: ${error.message}`,
        '',
        error.stack || '(no stack trace)',
        componentStack ? `\nComponent Stack:${componentStack}` : '',
    ].join('\n').trim();

    const handleCopy = () => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(errorText)
                .then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2500);
                })
                .catch(() => { });
        }
    };

    return (
        <div style={{ textAlign: 'left', maxWidth: 600, margin: '0 auto 8px' }}>
            <div style={{
                background: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: 4,
                padding: '8px 12px',
                fontSize: 13,
                wordBreak: 'break-word',
            }}>
                <strong>{error.name}</strong>: {error.message}
            </div>
            <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: 'pointer', color: '#6c757d', fontSize: 12 }}>
                    Stack trace (voor support / for support)
                </summary>
                <pre style={{
                    fontSize: 11,
                    background: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: 4,
                    padding: 8,
                    overflow: 'auto',
                    maxHeight: 180,
                    marginTop: 6,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    textAlign: 'left',
                }}>
                    {errorText}
                </pre>
            </details>
            <Button
                size="small"
                style={{ marginTop: 8, width: '100%' }}
                onClick={handleCopy}
            >
                {copied ? '✓ Gekopieerd / Copied' : '📋 Kopieer foutmelding / Copy error'}
            </Button>
        </div>
    );
}

export default ErrorBoundary;
